import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { ProgressSpinner } from "@openng/optimus-ui/progressspinner";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { IgnoredFaultCode } from "../../models/api/fault/ignoredFaultCode";
import { IgnoredFaultService } from "../../services/api/ignored-faults/ignored-faults.service";
import { first } from "rxjs";
import { SimplebarAngularModule } from "simplebar-angular";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DynamicDialogRef, DialogService } from "@openng/optimus-ui/dynamicdialog";
import { FaultIgnoreForm } from "./components/fault-ignore-form/fault-ignore-form";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { TableModule } from "@openng/optimus-ui/table";
import { Column } from "../../models/ui/tableColumn";

@Component({
  selector: "app-fault-ignore",
  imports: [
    HasPermissionDirective,
    TimeoutComponent,
    ProgressSpinner,
    TranslateModule,
    SimplebarAngularModule,
    SvgIconComponent,
    ButtonComponent,
    TableModule,
  ],
  templateUrl: "./fault-ignore.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./fault-ignore.component.scss",
})
export class FaultIgnoreComponent implements OnInit {
  public permissions = inject(PermissionService);

  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);
  public ignoredFaults: WritableSignal<IgnoredFaultCode[]> = signal([]);

  public faultCodeColumns: Column[] = [
    { field: "code", header: "ignored_fault_list.fault_code" },
    { field: "description", header: "ignored_fault_list.fault_description" },
  ];

  private ignoredFaultService = inject(IgnoredFaultService);
  private dialog = inject(DialogService);
  private ref: DynamicDialogRef | null = null;
  private translate = inject(TranslateService);
  private message = inject(MessageService);
  private confirmation = inject(ConfirmationService);

  public ngOnInit(): void {
    this.getIgnoredFaults();
  }

  /**
   * Loads all ignored fault codes from the server and updates the signal
   * @remark This method is called when the component is initialized.
   */
  private getIgnoredFaults() {
    this.loading.set(true);
    this.ignoredFaultService
      .getIgnoredFaults()
      .pipe(first())
      .subscribe({
        next: (ignoredFaults) => {
          this.ignoredFaults.set(ignoredFaults);
          this.loading.set(false);
        },
      });
  }

  /**
   * Opens the dialog for adding a new ignored fault
   */
  public onIgnoredFaultAdd() {
    this.openIgnoredFaultDialog("add");
  }

  /**
   * Opens the dialog for updating a new ignored fault
   */
  public onIgnoredFaultEdit(fault: IgnoredFaultCode) {
    this.openIgnoredFaultDialog("edit", fault);
  }

  /**
   * Opens a confirmation dialog for deleting a ignored fault from the list
   * @param fault The fault that is to be deleted
   * @param event The event that triggered the function
   */
  public onIgnoredFaultDelete(fault: IgnoredFaultCode, event: Event) {
    event.stopPropagation();
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("ignored_fault_list.confirm_delete"),
      key: "global",
      accept: () => {
        this.ignoredFaultService.deleteIgnoredFault(fault.id).subscribe({
          next: (response) => {
            if (response) {
              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant(`ignored_fault_list.delete_fault_success`),
              });
              this.getIgnoredFaults();
            }
          },
        });
      },
    });
  }

  /**
   * Opens a dialog for either updating or adding a fault on the list.
   * @param actionType Defines if the dialog is for updating or adding a fault
   * @param fault (Optional): The fault that is to be updated
   */
  private openIgnoredFaultDialog(actionType: "add" | "edit", fault?: IgnoredFaultCode) {
    this.ref = this.dialog.open(FaultIgnoreForm, {
      data: {
        actionType: actionType,
        fault: fault,
      },
      header: this.translate.instant(`ignored_fault_list.${actionType}_ignored_fault`),
      modal: true,
      width: "40vw",
      contentStyle: { overflow: "auto", padding: "20px 100px" },
      breakpoints: {
        "960px": "35vw",
        "640px": "45vw",
      },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.getIgnoredFaults();
      }
    });
  }
}
